/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { isElementVisible } from "@/utils/isElementVisible"
import { css } from "vite-css-in-js"
import { shallowRef, toRef, watch, type HTMLAttributes, type VNodeChild } from "vue"
import { defineComponent, inferProps, type Component, type ReactiveComponent } from "vue-utils"
import type { Nullable } from "../../models"

type Key = string | number

interface Props<in out T> {
	options: T[]

	getKey: (option: T) => Key
	getTitle: (option: T) => string
	renderOption: (option: T) => VNodeChild

	noOptionsText: string | null

	highlightedOption: Nullable<T>
	setHighlightedOption(option: Nullable<T>): void
	selectOption(option: T): void
}

const dropdownStyles = css`
	position: absolute;
	background-color: #fafafa;
	box-shadow: 0.25rem 0.25rem 0.25rem #b8b8b8;
	border-radius: 0 0 0.35rem 0.35rem;
	border: 1px solid rgba(0, 0, 0, 0.35);
	overflow-y: auto;
	z-index: 1;
	font-weight: normal;

	ul {
		list-style: none;
		padding: 0;
		margin: 0;
	}

	button {
		all: unset;
		width: 100%;
		max-width: calc(100% - 1rem);
		text-overflow: ellipsis;
		overflow: hidden;
		white-space: nowrap;
		padding: 0.25rem 0.5rem;

		&[data-selected="true"] {
			cursor: pointer;
			background-color: #d6d6d6;
		}
	}
`

export default function createVirtualDropdownBox<T>(): Component<Props<T>, HTMLAttributes> {
	const DropdownBox: ReactiveComponent<Props<T>, HTMLAttributes> = (props, { attrs }) => {
		const containerRef = shallowRef<HTMLElement>()

		watch(toRef(props, "highlightedOption"), (o) => {
			const option = o as T | null
			if (!option || !containerRef.value) {
				return
			}
			const expectedKey = props.getKey(option)
			for (const li of containerRef.value.querySelectorAll("li")) {
				if (li.dataset.key === expectedKey && !isElementVisible(li)) {
					li.scrollIntoView({ behavior: "instant", block: "nearest" })
				}
			}
		})

		return () => (
			<div class={dropdownStyles} {...attrs}>
				<ul
					ref={containerRef}
					onMousedown={(e) => e.preventDefault()}
					onMouseleave={() => props.setHighlightedOption(null)}
				>
					{props.options.length === 0 && props.noOptionsText !== null && (
						<li>
							<button tabindex={-1} disabled>
								<i>{props.noOptionsText}</i>
							</button>
						</li>
					)}
					{props.options.map((option) => (
						<li
							key={props.getKey(option)}
							title={props.getTitle(option)}
							data-key={props.getKey(option)}
							onMousemove={() => props.setHighlightedOption(option)}
						>
							<button
								tabindex={-1}
								onClick={(e) => e.button === 0 && props.selectOption(option)}
								data-selected={String(props.highlightedOption === option)}
							>
								{props.renderOption(option)}
							</button>
						</li>
					))}
				</ul>
			</div>
		)
	}

	return defineComponent(DropdownBox, inferProps<Props<T>>())
}
